import { useQuery as useUrqlQuery } from 'urql';

import { ENTITY_V3_QUERY, EntityV3Type } from '@revelio/data-access';

const COMPETITOR_COUNT = 5;

export type CompanyCompetitor = { rcid: number; name: string };

/**
 * A company's competitors by rcid, from `entityV3.competitors`. The one place
 * this request is defined: the company tooltip reads it directly and the
 * picker's shortcut panel through `useCompetitorSuggestions`, so both resolve to
 * a single cache entry instead of asking for the same company twice. Paused
 * until `enabled`.
 */
export const useCompanyCompetitors = (
  rcid: number | null,
  {
    enabled,
    competitorCount = COMPETITOR_COUNT,
  }: { enabled: boolean; competitorCount?: number }
): {
  competitors: CompanyCompetitor[];
  isLoading: boolean;
  hasError: boolean;
} => {
  const [{ data, fetching, error }] = useUrqlQuery({
    query: ENTITY_V3_QUERY,
    variables: {
      req: {
        entID: rcid ?? 0,
        entType: EntityV3Type.Company,
        numCompetitors: competitorCount,
      },
    },
    pause: !enabled || rcid === null,
    requestPolicy: 'cache-first',
  });

  const competitors: CompanyCompetitor[] = (data?.entityV3?.competitors ?? [])
    .flatMap((competitor) => {
      const competitorId = Number(competitor?.id);
      return competitor &&
        competitor.shortName.length > 0 &&
        Number.isFinite(competitorId) &&
        competitorId > 0
        ? [{ rcid: competitorId, name: competitor.shortName }]
        : [];
    })
    .slice(0, competitorCount);

  return {
    competitors,
    isLoading: enabled && fetching,
    hasError: enabled && error !== undefined,
  };
};
